import { useState } from "react";
import { Box, Chip } from "@mui/material";
import moment from "moment";

import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";
import { findSelectedPreset, getAllowedReportDatePresets, type ReportDatePreset } from "src/utils/report/reportDatePresets.util";
import { getMaxToDate, getMinFromDate } from "src/utils/report/reportRange.util";

import { InputField } from "../v3";

const DATE_FORMAT = "YYYY-MM-DD";
const isValidDate = (value: string): boolean => moment(value, DATE_FORMAT, true).isValid();

/** What the form's date handler reads from an input change: the field name and its new `YYYY-MM-DD` value. */
export interface DateChangeEvent {
  target: { name: string; value: string };
}

interface ReportDateRangeFieldsProps {
  fromDate: string;
  toDate: string;
  onChange: (event: DateChangeEvent) => void;
  errors?: { fromDate?: string; toDate?: string };
}

/**
 * Quick-pick buttons and the From / To date inputs of a report form. The pickers stop at the longest range the
 * company plan allows (`useReportRangeLimit`); the plan range itself is not spelled out. The quick picks that
 * do not fit the plan are left out. Only one quick pick is highlighted, even when two cover the same dates.
 */
export const ReportDateRangeFields = ({ fromDate, toDate, onChange, errors = {} }: ReportDateRangeFieldsProps) => {
  const { months } = useReportRangeLimit();
  const [lastClickedId, setLastClickedId] = useState<ReportDatePreset["id"]>();

  const minFromDate = isValidDate(toDate) ? getMinFromDate(toDate, months) : undefined;
  const maxToDate = isValidDate(fromDate) ? getMaxToDate(fromDate, months) : undefined;
  const presets = getAllowedReportDatePresets(moment().format(DATE_FORMAT), months);

  const selectedPreset = findSelectedPreset(presets, fromDate, toDate, lastClickedId);

  const selectPreset = (preset: ReportDatePreset) => {
    setLastClickedId(preset.id);
    onChange({ target: { name: "fromDate", value: preset.fromDate } });
    onChange({ target: { name: "toDate", value: preset.toDate } });
  };

  return (
    <>
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {presets.map((preset) => {
          const selected = preset.id === selectedPreset?.id;

          return (
            <Chip
              key={preset.id}
              label={preset.label}
              size="small"
              clickable
              variant={selected ? "filled" : "outlined"}
              onClick={() => selectPreset(preset)}
              sx={
                selected
                  ? { backgroundColor: "#674BF5", color: "#fff", "&:hover": { backgroundColor: "#5a40d6" } }
                  : { borderColor: "#674BF5", color: "#674BF5" }
              }
            />
          );
        })}
      </Box>
      <Box display="flex" gap={2}>
        <InputField
          required
          type="date"
          label="From Date"
          name="fromDate"
          value={fromDate}
          onChange={onChange}
          error={!!errors.fromDate}
          helperText={errors.fromDate}
          inputSlotProps={{ htmlInput: { min: minFromDate } }}
          fullWidth
        />
        <InputField
          required
          type="date"
          label="To Date"
          name="toDate"
          value={toDate}
          onChange={onChange}
          error={!!errors.toDate}
          helperText={errors.toDate}
          inputSlotProps={{ htmlInput: { max: maxToDate } }}
          fullWidth
        />
      </Box>
    </>
  );
};
